import { useState, useEffect, useContext, useMemo } from "react";
import {
  Layout,
  theme,
  Button,
  Input,
  Modal,
  Form,
  InputNumber,
  DatePicker,
  Select,
  Table,
  Tag,
  Card,
  Row,
  Col,
  Statistic,
  Dropdown,
  message,
} from "antd";
import type { MenuProps } from "antd";
import {
  DollarOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BankOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import LoadingOverlay from "react-loading-overlay-ts";
import dayjs from "dayjs";

import TopBar from "../../components/TopBar";
import SideBar from "../../components/SideBar";

import { AuthContext } from "../../context/AuthContext";

import {
  getGroupPaymentsApi,
  addGroupPaymentApi,
  editGroupPaymentApi,
  removeGroupPaymentApi,
} from "../../services/Payment/GroupPaymentApis";

const { Content } = Layout;
const { Search } = Input;

type PaymentDirection = "Received" | "Paid Out";

type Payment = {
  id: string;
  Reference: string;
  GroupID: number;
  GroupName: string;
  Amount: number;
  Direction: PaymentDirection;
  PaymentDate: string;
  PaymentMethod: string;
  Notes?: string;
};

const PAYMENT_METHODS = ["EFT", "Cash", "Card", "DebiCheck"];
const DIRECTIONS: PaymentDirection[] = ["Received", "Paid Out"];

const signedAmount = (p: Payment) =>
  p.Direction === "Paid Out" ? -Math.abs(p.Amount) : Math.abs(p.Amount);

const mapApiPayment = (record: any): Payment => ({
  id: String(record.id),
  Reference: record.Reference ?? "",
  GroupID: Number(record.GroupID ?? record.Group?.id ?? 0),
  GroupName: record.Group?.GroupName ?? "",
  Amount: Number(record.Amount ?? 0),
  Direction: (record.Direction as PaymentDirection) ?? "Received",
  PaymentDate: record.Date ?? "",
  PaymentMethod: record.Method ?? "",
  Notes: record.Notes ?? undefined,
});

const PaymentsPage: React.FC = () => {
  const { currentUser, currentGroup } = useContext(AuthContext);

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const [isActive, setActive] = useState(true);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Payment | null>(null);
  const [form] = Form.useForm();

  const role = currentUser?.Role?.Name ?? "";
  const isSuperAdmin = role === "Super Admin";

  const activeGroup = useMemo(
    () =>
      (currentUser?.Group ?? []).find(
        (g: any) => String(g.id) === String(currentGroup)
      ),
    [currentUser, currentGroup]
  );
  const activeGroupName = activeGroup?.GroupName ?? "";

  useEffect(() => {
    if (!currentGroup) {
      setPayments([]);
      setActive(false);
      return;
    }
    setActive(true);
    getGroupPaymentsApi(currentGroup)
      .then((res: any) => {
        const list = Array.isArray(res?.data) ? res.data.map(mapApiPayment) : [];
        setPayments(list);
      })
      .catch((err: any) => {
        console.error(err);
        message.error("Failed to load payments");
      })
      .finally(() => setActive(false));
  }, [currentGroup]);

  const filtered = useMemo(() => {
    if (!searchValue) return payments;
    const v = searchValue.toLowerCase();
    return payments.filter(
      (p) =>
        p.Reference.toLowerCase().includes(v) ||
        p.GroupName.toLowerCase().includes(v) ||
        p.PaymentMethod.toLowerCase().includes(v) ||
        p.Direction.toLowerCase().includes(v)
    );
  }, [payments, searchValue]);

  const totals = useMemo(() => {
    return filtered.reduce(
      (acc, p) => {
        const amount = Math.abs(p.Amount);
        if (p.Direction === "Received") acc.received += amount;
        else acc.paidOut += amount;
        acc.total += signedAmount(p);
        return acc;
      },
      { total: 0, received: 0, paidOut: 0 }
    );
  }, [filtered]);

  const openAddModal = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      PaymentDate: dayjs(),
      PaymentMethod: "EFT",
      Direction: "Received",
      GroupName: activeGroupName,
    });
    setShowModal(true);
  };

  const openEditModal = (record: Payment) => {
    setEditing(record);
    form.setFieldsValue({
      ...record,
      PaymentDate: dayjs(record.PaymentDate),
    });
    setShowModal(true);
  };

  const handleSubmit = () => {
    form.validateFields().then(async (values) => {
      if (!currentGroup) {
        message.error("No group selected");
        return;
      }
      const payload = {
        GroupID: Number(currentGroup),
        Direction: values.Direction as PaymentDirection,
        Amount: Math.abs(Number(values.Amount)),
        Date: values.PaymentDate.toISOString(),
        Method: values.PaymentMethod,
        Notes: values.Notes,
      };

      setActive(true);
      try {
        if (editing) {
          const res = await editGroupPaymentApi(editing.id, payload);
          const updated = mapApiPayment(res?.data ?? {});
          setPayments((prev) =>
            prev.map((p) => (p.id === editing.id ? updated : p))
          );
          message.success("Payment updated");
        } else {
          const res = await addGroupPaymentApi(payload);
          const created = mapApiPayment(res?.data ?? {});
          setPayments((prev) => [created, ...prev]);
          message.success("Payment added");
        }
        setShowModal(false);
        form.resetFields();
        setEditing(null);
      } catch (err) {
        console.error(err);
        message.error("Failed to save payment");
      } finally {
        setActive(false);
      }
    });
  };

  const handleDelete = async (id: string) => {
    setActive(true);
    try {
      await removeGroupPaymentApi(id);
      setPayments((prev) => prev.filter((p) => p.id !== id));
      message.success("Payment removed");
    } catch (err) {
      console.error(err);
      message.error("Failed to remove payment");
    } finally {
      setActive(false);
    }
  };

  const getRowMenu = (record: Payment): MenuProps["items"] => [
    {
      key: "edit",
      icon: <EditOutlined />,
      label: "Edit",
      onClick: () => openEditModal(record),
    },
    {
      key: "remove",
      icon: <DeleteOutlined />,
      label: "Remove",
      danger: true,
    },
  ];

  const columns = [
    {
      title: "Reference",
      dataIndex: "Reference",
      sorter: (a: Payment, b: Payment) =>
        a.Reference.localeCompare(b.Reference),
    },
    {
      title: "Group",
      dataIndex: "GroupName",
      sorter: (a: Payment, b: Payment) =>
        a.GroupName.localeCompare(b.GroupName),
    },
    {
      title: "Direction",
      dataIndex: "Direction",
      render: (val: PaymentDirection) => (
        <Tag color={val === "Paid Out" ? "red" : "green"}>{val}</Tag>
      ),
      filters: DIRECTIONS.map((d) => ({ text: d, value: d })),
      onFilter: (value: any, record: Payment) => record.Direction === value,
    },
    {
      title: "Amount (R)",
      dataIndex: "Amount",
      sorter: (a: Payment, b: Payment) => signedAmount(a) - signedAmount(b),
      render: (_: number, record: Payment) => {
        const value = signedAmount(record);
        const isNegative = value < 0;
        return (
          <span
            style={{
              fontWeight: 600,
              color: isNegative ? "#cf1322" : "#389e0d",
            }}
          >
            {isNegative ? "-" : ""}R{Math.abs(value).toFixed(2)}
          </span>
        );
      },
    },
    {
      title: "Method",
      dataIndex: "PaymentMethod",
    },
    {
      title: "Notes",
      dataIndex: "Notes",
      render: (val: string) => val || "-",
    },
    {
      title: "Date",
      dataIndex: "PaymentDate",
      sorter: (a: Payment, b: Payment) =>
        new Date(a.PaymentDate).getTime() - new Date(b.PaymentDate).getTime(),
      defaultSortOrder: "descend" as const,
      render: (val: string) =>
        val ? dayjs(val).format("YYYY-MM-DD") : "-",
    },
    ...(isSuperAdmin
      ? [
          {
            title: "Actions",
            width: 80,
            render: (_: any, record: Payment) => (
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: getRowMenu(record),
                  onClick: ({ key, domEvent }) => {
                    domEvent.stopPropagation();
                    if (key === "edit") {
                      openEditModal(record);
                    } else if (key === "remove") {
                      Modal.confirm({
                        title: "Remove this payment?",
                        okText: "Remove",
                        okButtonProps: { danger: true },
                        onOk: () => handleDelete(record.id),
                      });
                    }
                  },
                }}
              >
                <Button
                  size="small"
                  type="text"
                  icon={<MoreOutlined />}
                  onClick={(e) => e.stopPropagation()}
                />
              </Dropdown>
            ),
          },
        ]
      : []),
  ];

  return (
    <LoadingOverlay active={isActive} spinner>
      <Layout style={{ minHeight: "100vh" }}>
        <SideBar />
        <Layout className="site-layout">
          <TopBar />
          <Content>
            <div
              style={{
                padding: 24,
                minHeight: "100%",
                background: colorBgContainer,
              }}
            >
              <h2>
                <DollarOutlined
                  style={{ paddingRight: "5px", color: "#f04f05" }}
                />
                Payments
              </h2>

              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={8}>
                  <Card>
                    <Statistic
                      title="Total"
                      value={totals.total}
                      precision={2}
                      prefix="R"
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card>
                    <Statistic
                      title="Received"
                      value={totals.received}
                      precision={2}
                      prefix={
                        <ArrowDownOutlined style={{ color: "#52c41a" }} />
                      }
                      valueStyle={{ color: "#389e0d" }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card>
                    <Statistic
                      title="Paid Out"
                      value={totals.paidOut}
                      precision={2}
                      prefix={
                        <ArrowUpOutlined style={{ color: "#cf1322" }} />
                      }
                      valueStyle={{ color: "#cf1322" }}
                    />
                  </Card>
                </Col>
              </Row>

              <div className="interactions">
                {isSuperAdmin && (
                  <Button onClick={openAddModal} size="large">
                    <PlusOutlined style={{ paddingRight: "5px" }} />
                    Add Payment
                  </Button>
                )}

                <div>
                  <Search
                    placeholder="Search payments"
                    size="large"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    style={{ width: "100%", margin: "24px 0" }}
                  />
                </div>
              </div>

              <Table
                dataSource={filtered}
                columns={columns}
                rowKey="id"
                pagination={{ pageSize: 20, hideOnSinglePage: true }}
                scroll={{ x: true }}
              />
            </div>
          </Content>
        </Layout>
      </Layout>

      <Modal
        title={
          <span>
            <BankOutlined style={{ color: "#f04f05", paddingRight: 8 }} />
            {editing ? "Edit Payment" : "Add Payment"}
          </span>
        }
        open={showModal}
        onOk={handleSubmit}
        onCancel={() => {
          setShowModal(false);
          setEditing(null);
          form.resetFields();
        }}
        okText={editing ? "Save" : "Add"}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            label="Group"
            name="GroupName"
            rules={[{ required: true, message: "Group is required" }]}
          >
            <Input placeholder="External group name" disabled={!editing} />
          </Form.Item>

          <Form.Item
            label="Direction"
            name="Direction"
            rules={[{ required: true, message: "Direction is required" }]}
            tooltip="Received = group received from external; Paid Out = group paid out to external"
          >
            <Select
              options={DIRECTIONS.map((d) => ({ value: d, label: d }))}
            />
          </Form.Item>

          <Form.Item
            label="Amount (R)"
            name="Amount"
            rules={[{ required: true, message: "Amount is required" }]}
          >
            <InputNumber
              min={0}
              step={0.01}
              style={{ width: "100%" }}
              placeholder="0.00"
            />
          </Form.Item>

          <Form.Item
            label="Payment Date"
            name="PaymentDate"
            rules={[{ required: true, message: "Payment date is required" }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            label="Payment Method"
            name="PaymentMethod"
            rules={[{ required: true }]}
          >
            <Select
              options={PAYMENT_METHODS.map((m) => ({ value: m, label: m }))}
            />
          </Form.Item>

          <Form.Item label="Notes" name="Notes">
            <Input.TextArea rows={3} placeholder="Optional" />
          </Form.Item>
        </Form>
      </Modal>
    </LoadingOverlay>
  );
};

export default PaymentsPage;
